import React, { useEffect, useMemo } from 'react';

import { useLogger } from '@opengov/psc-react-logger';
import { isEmpty, isUndefined } from 'lodash-es';
import { useNavAuth } from '../../ContextSwitcher/Context/NavAuthProvider';
import { authenticatedApiFetch, useFetchState } from '../../ContextSwitcher/Context/fetchUtils';
import { useNavBarProps } from '../../TopLevelNavigation/NavBarPropsContext';

/**
 * GraphQL query to fetch entity by friendly ID
 */
const getEntityByFriendlyIdQuery = `
query entityByFriendlyId($friendlyId: String!) {
  queryEntityForSwitcherById(queryAction: BY_FRIENDLY_ID, queryValue: $friendlyId) {
    id
    friendlyId
    entityId
  }
}
`;

/**
 * Response interface for the entity UUID GraphQL API
 */
interface IEntityUUIDGraphQlResponse {
  data: {
    queryEntityForSwitcherById: {
      id: string;
      friendlyId: string;
      entityId: string;
    };
  };
  errors?: Array<{
    message: string;
    locations?: Array<{
      line: number;
      column: number;
    }>;
    path?: string[];
  }>;
}

/**
 * Response interface for the entity UUID API (legacy)
 */
interface IEntityUUIDResponse {
  uuid: string;
}

/**
 * Entity context value interface
 */
interface IEntityContextValue {
  entityUUID?: string;
  loading: boolean;
  loadingError: boolean;
}

/**
 * Entity context
 */
const EntityContext = React.createContext<IEntityContextValue | undefined>(undefined);

/**
 * Hook to access entity context
 * @returns Entity context value containing entityUUID, loading state, and error state
 * @throws Error if used outside of EntityProvider
 */
export const useEntity = (): IEntityContextValue => {
  const context = React.useContext(EntityContext);

  if (context === undefined) {
    throw new Error('useEntity must be used within an EntityProvider');
  }

  return context;
};

/**
 * Fetches entity UUID from the API using GraphQL
 */
const fetchEntityUUIDFromAPI = async (
  baseUrl: string,
  friendlyId: string,
  authBearerToken?: string
): Promise<IEntityUUIDResponse> => {
  const base = baseUrl ?? window.location.origin;
  const graphqlUrl = `${base}/api/access/graphql`;

  const data = await authenticatedApiFetch<IEntityUUIDGraphQlResponse>(graphqlUrl, authBearerToken, {
    method: 'POST',
    body: {
      query: getEntityByFriendlyIdQuery,
      variables: { friendlyId: friendlyId }
    },
    includeErrorHandling: false // GraphQL errors are handled in the response
  });

  // Check for GraphQL errors
  if (data.errors && data.errors.length > 0) {
    throw new Error(`GraphQL errors: ${data.errors.map((error) => error.message).join(', ')}`);
  }

  // Check if the entity was found
  if (!data.data?.queryEntityForSwitcherById) {
    throw new Error('Entity not found');
  }

  // Return the entity ID as the UUID (mapping from GraphQL response to legacy interface)
  return {
    uuid: data.data.queryEntityForSwitcherById.id
  };
};

/**
 * Entity provider component that manages entity UUID fetching
 *
 * This provider fetches the entity UUID for a given friendly ID from the control panel API.
 * It leverages the NavBar props to get the necessary configuration and automatically
 * handles the fetching logic based on the provided options.
 */
export const EntityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const logger = useLogger();
  const { authBearerToken } = useNavAuth();
  const { unstable_ogAgentOptions } = useNavBarProps();

  const {
    isMounted,
    isFetchingData,
    loading,
    loadingError,
    setLoading,
    setFetching,
    handleFetchError,
    handleFetchSuccess
  } = useFetchState();

  // State for the entity UUID
  const [entityUUID, setEntityUUID] = React.useState<string>();

  // Extract configuration from NavBar props
  const { friendlyId, platformBaseUrl, entityUUID: providedEntityUUID } = unstable_ogAgentOptions ?? {};

  const noOgAgentOptions = useMemo(
    () => isUndefined(unstable_ogAgentOptions) || isEmpty(unstable_ogAgentOptions),
    [unstable_ogAgentOptions]
  );

  const handleFetchResponse = (data: IEntityUUIDResponse) => {
    if (!isMounted.current || !data) return;

    if (data.uuid) {
      setEntityUUID(data.uuid);
      handleFetchSuccess();
    } else {
      throw new Error('No UUID found in response');
    }
  };

  useEffect(() => {
    // If there are no Og Agent options, there is nothing to fetch
    if (noOgAgentOptions) return;

    // Use provided entity UUID if available
    if (providedEntityUUID) {
      setEntityUUID(providedEntityUUID);
      return;
    }

    // Return early if no friendlyId is provided
    if (!friendlyId) {
      console.error('No friendly id provided. Please provide a friendly id or an entity UUID.');
      return;
    }

    // Return early if no auth token is available
    if (!authBearerToken) {
      console.error('No auth token available.');
      return;
    }

    const fetchEntityUUIDData = async () => {
      // Prevent multiple simultaneous fetches
      if (isFetchingData.current) return;

      setFetching(true);
      setLoading(true);
      logger.info('Fetching entity UUID', {
        friendlyId,
        hasToken: !!authBearerToken,
        platformBaseUrl
      });

      try {
        const data = await fetchEntityUUIDFromAPI(platformBaseUrl, friendlyId, authBearerToken);

        logger.info('Successfully fetched entity UUID', {
          friendlyId,
          hasData: !!data?.uuid
        });
        return data;
      } catch (error) {
        logger.error('Failed to fetch entity UUID', {
          error,
          friendlyId,
          platformBaseUrl
        });
        throw error;
      } finally {
        setFetching(false);
      }
    };

    fetchEntityUUIDData()
      .then(handleFetchResponse)
      .catch((error) => {
        handleFetchError(error, 'entity UUID response');
      });
  }, [
    providedEntityUUID,
    friendlyId,
    platformBaseUrl,
    authBearerToken,
    logger,
    isMounted,
    isFetchingData,
    noOgAgentOptions,
    setFetching,
    setLoading,
    handleFetchError,
    handleFetchSuccess
  ]);

  const contextValue: IEntityContextValue = {
    entityUUID,
    loading,
    loadingError
  };

  return <EntityContext.Provider value={contextValue}>{children}</EntityContext.Provider>;
};
